<script setup lang="ts">
import type { ContentTocLink } from "@nuxt/ui";
import type { WikiItem, WikiVehicle } from "~~/types/gamedata";
import type { ShipCompareShip } from "~~/types/shipexkurs";

definePageMeta({ layout: "shipexkurs" });

const route = useRoute();
const router = useRouter();
const nuxtApp = useNuxtApp();

function parseShipsQuery(value: unknown): string[] {
  if (typeof value !== "string" || !value) return [];
  return [...new Set(value.split(",").map((entry) => entry.trim()).filter(Boolean))];
}

const initialShipIds = parseShipsQuery(route.query.ships);
// Mind. 2 Slots von Anfang an sichtbar, damit sofort klar ist, dass hier
// verglichen wird — bei einem geteilten Link (Query-Param) reichen die dort
// enthaltenen Schiffe.
const slots = ref<(string | null)[]>(initialShipIds.length ? [...initialShipIds] : [null, null]);

const selectedShipIds = computed(() => slots.value.filter((id): id is string => id != null));

// Zuletzt verglichene Schiffe überleben einen Reload auch ohne geteilten Link.
// Die URL-Query hat beim Laden Vorrang (teilbarer Link soll nicht von einer
// älteren lokalen Auswahl überschrieben werden).
const COMPARE_STORAGE_KEY = "shipexkurs:compare:selected-ships";

function readStoredShipIds(): string[] {
  if (!import.meta.client) return [];
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

onMounted(() => {
  if (initialShipIds.length) return;
  const stored = readStoredShipIds();
  if (stored.length) slots.value = [...stored];
});

// State → URL, nicht umgekehrt: die Query dient nur als Startzustand beim
// Laden der Seite (teilbarer Link), nicht als fortlaufende Quelle der Wahrheit.
watch(selectedShipIds, (ids) => {
  const query = { ...route.query };
  if (ids.length) query.ships = ids.join(",");
  else delete query.ships;
  void router.replace({ query });

  if (import.meta.client) {
    if (ids.length) localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
    else localStorage.removeItem(COMPARE_STORAGE_KEY);
  }
}, { deep: true });

function addSlot() {
  slots.value.push(null);
}

function removeSlot(index: number) {
  slots.value.splice(index, 1);
}

function clearSelection() {
  slots.value = [null, null];
  if (import.meta.client) localStorage.removeItem(COMPARE_STORAGE_KEY);
}

const { containerRef: slotsContainerRef, isDragging: slotsDragging, onPointerDown: onSlotsPointerDown, onPointerMove: onSlotsPointerMove, onPointerUp: onSlotsPointerUp, onClickCapture: onSlotsClickCapture } = useDragScroll();

const { data: vehicleIndex, pending: vehicleIndexPending } = useVehicleIndex({ lazy: true });

/** Ein Schiff taucht nie gleichzeitig in mehreren Slots auf. */
function vehiclesForSlot(index: number) {
  const chosenElsewhere = new Set(slots.value.filter((id, i) => i !== index && id != null));
  return vehicleIndex.value.filter((vehicle) => !chosenElsewhere.has(vehicle.id));
}

// Rotierende Spaltenfarbe — dieselbe Palette wie die Ring-Charts in
// `ship/table/tab-content.vue` (`DEFAULT_RING_COLORS`), für ein konsistentes
// Farbschema über die ganze Seite.
const COLOR_PALETTE = ["var(--color-aris-400)", "var(--color-industrial-400)", "#60a5fa", "#c084fc"];
function colorForIndex(index: number): string {
  return COLOR_PALETTE[index % COLOR_PALETTE.length]!;
}

interface ShipLoadState {
  raw: WikiVehicle | null;
  rawPending: boolean;
  items: Map<string, WikiItem>;
  itemsPending: boolean;
}

const shipLoadStates = reactive(new Map<string, ShipLoadState>());

async function loadShip(id: string) {
  if (shipLoadStates.has(id)) return;

  const state = reactive<ShipLoadState>({ raw: null, rawPending: true, items: new Map(), itemsPending: false });
  shipLoadStates.set(id, state);

  // `runWithContext` nötig: dies ist ein manueller async `watch`-Callback, kein
  // `useAsyncData`-Handler — der Nuxt-Kontext geht nach einem `await` sonst
  // verloren ("called outside of a plugin, ... Vue setup function"), siehe
  // Kommentar bei `variantLoadout` in `ships/[id].vue`, live geprüft.
  const raw = await nuxtApp.runWithContext(() => fetchVehicleRaw(id));
  // Zwischenzeitlich wieder entfernt (Nutzer hat den Slot geleert) — Ergebnis verwerfen.
  if (!shipLoadStates.has(id)) return;

  state.raw = raw;
  state.rawPending = false;

  if (raw?.ports?.length) {
    state.itemsPending = true;
    try {
      const items = await nuxtApp.runWithContext(() => resolvePortItems(raw.ports));
      if (shipLoadStates.has(id)) state.items = items;
    } finally {
      if (shipLoadStates.has(id)) state.itemsPending = false;
    }
  }
}

watch(
  selectedShipIds,
  (ids) => {
    for (const id of ids) {
      if (!shipLoadStates.has(id)) void loadShip(id);
    }
    for (const id of [...shipLoadStates.keys()]) {
      if (!ids.includes(id)) shipLoadStates.delete(id);
    }
  },
  { immediate: true },
);

const ships = computed<ShipCompareShip[]>(() =>
  selectedShipIds.value
    .map((id, index) => {
      const summary = vehicleIndex.value.find((vehicle) => vehicle.id === id);
      if (!summary) return null;
      const state = shipLoadStates.get(id);
      return {
        id,
        summary,
        raw: state?.raw ?? null,
        rawPending: state?.rawPending ?? true,
        items: state?.items ?? new Map(),
        itemsPending: state?.itemsPending ?? false,
        color: colorForIndex(index),
      } satisfies ShipCompareShip;
    })
    .filter((ship): ship is ShipCompareShip => ship !== null),
);

/** Für `UContentToc` — dieselben Anker-IDs wie die `<section>`s/Karten unten
 * (Ausrüstungs-Kategorien: siehe `getComponentCategoryAnchorId()` in
 * `component-comparison.vue`, damit beide garantiert übereinstimmen). */
const componentCategories = computed(() => getPresentComponentCategories(ships.value));

const tocLinks = computed<ContentTocLink[]>(() => [
  { id: "fahrzeugdaten", depth: 2, text: "Fahrzeugdaten" },
  {
    id: "ausruestung",
    depth: 2,
    text: "Ausrüstung",
    children: componentCategories.value.map((category) => ({ id: category.id, depth: 3, text: category.label })),
  },
]);

/**
 * `UContentToc` navigiert Anker intern über `router.push()`. Auf dieser Seite
 * scrollen wir stattdessen direkt zum bereits gerenderten Abschnitt.
 */
function scrollFromToc(event: MouseEvent) {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest<HTMLAnchorElement>('a[href^="#"]');
  const id = link?.getAttribute("href")?.slice(1);
  const target = id ? document.getElementById(id) : null;
  if (!target) return;

  event.preventDefault();
  event.stopPropagation();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
}

useSeoMeta({
  title: "ShipExkurs – Schiffsvergleich | ArisCorp",
  description: "Vergleiche Star-Citizen-Schiffe direkt nebeneinander — Kennzahlen, Ausrüstung und Verteidigung im Überblick.",
  ogTitle: "ShipExkurs – Schiffsvergleich",
  ogDescription: "Vergleiche Star-Citizen-Schiffe direkt nebeneinander.",
});
</script>

<template>
  <div class="space-y-6 px-4 py-8 sm:px-6 lg:px-8">
    <div>
      <h1 class="!my-0 text-2xl font-bold text-white">Schiffsvergleich</h1>
      <p class="!my-0 text-sm text-neutral-400">
        {{ vehicleIndexPending ? 'Fahrzeugkatalog wird geladen…' : 'Wähle mindestens zwei Schiffe zum Vergleichen.' }}
      </p>
    </div>

    <div
      ref="slotsContainerRef"
      class="flex items-start gap-3 overflow-x-auto pb-1 select-none"
      :class="slotsDragging ? 'cursor-grabbing' : undefined"
      @pointerdown="onSlotsPointerDown"
      @pointermove="onSlotsPointerMove"
      @pointerup="onSlotsPointerUp"
      @pointercancel="onSlotsPointerUp"
      @click.capture="onSlotsClickCapture"
    >
      <div v-for="(slotId, index) in slots" :key="index" class="w-64 shrink-0">
        <ShipexkursCompareShipSlot
          :model-value="slotId"
          :vehicles="vehiclesForSlot(index)"
          :color="colorForIndex(index)"
          :removable="index > 0"
          @update:model-value="slots[index] = $event"
          @remove="removeSlot(index)"
        />
      </div>
      <UButton
        icon="i-lucide-plus"
        label="Schiff hinzufügen"
        color="neutral"
        variant="outline"
        size="lg"
        class="h-fit shrink-0"
        @click="addSlot"
      />
      <UButton
        v-if="selectedShipIds.length"
        icon="i-lucide-trash-2"
        label="Auswahl zurücksetzen"
        color="neutral"
        variant="ghost"
        size="lg"
        class="h-fit shrink-0"
        @click="clearSelection"
      />
    </div>

    <UEmptyState
      v-if="ships.length < 2"
      icon="i-lucide-scale"
      title="Noch keine zwei Schiffe ausgewählt"
      description="Wähle oben mindestens zwei Schiffe, um ihre Kennzahlen und Ausrüstung nebeneinander zu sehen."
    />

    <template v-else>
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_240px]">
        <div class="min-w-0 space-y-6">
          <section class="space-y-3">
            <h2 id="fahrzeugdaten" class="!my-0 scroll-mt-16 text-lg font-semibold text-white">Fahrzeugdaten</h2>
            <ShipexkursCompareStatsTable :ships="ships" />
          </section>

          <section class="space-y-3">
            <h2 id="ausruestung" class="!my-0 scroll-mt-16 text-lg font-semibold text-white">Ausrüstung</h2>
            <ShipexkursCompareComponentComparison :ships="ships" />
          </section>
        </div>

        <UContentToc
          :links="tocLinks"
          title="Auf dieser Seite"
          highlight
          highlight-variant="circuit"
          class="hidden sticky top-14 lg:block"
          @click.capture="scrollFromToc"
        />
      </div>
    </template>
  </div>
</template>
